import { fg } from '@atlaskit/platform-feature-flags';
import { startLighthouseObserver } from '../additional-payload';
import { setUFOConfig } from '../config';
import { experimentalVC, sinkExperimentalHandler } from '../create-experimental-interaction-metrics-payload';
import { setupHiddenTimingCapture } from '../hidden-timing';
import { interactionExtraMetrics, postInteractionLog, sinkInteractionHandler, sinkPostInteractionLogHandler } from '../interaction-metrics';
import { getPerformanceObserver } from '../interactions-performance-observer';
import { initialiseMemoryObserver, initialisePressureObserver } from '../machine-utilisation';
import scheduleIdleCallback from './schedule-idle-callback';
let initialized = false;
function sinkInteraction(instance, payloadPackage) {
  function sinkFn(interactionId, interaction) {
    function onIdle() {
      payloadPackage.createPayloads(interactionId, interaction).then(payloads => {
        // NOTE: This API is used by the UFO DevTool Chrome Extension and Criterion
        const devToolObserver = globalThis.__ufo_devtool_onUfoPayload;
        payloads === null || payloads === void 0 ? void 0 : payloads.forEach(payload => {
          if (typeof devToolObserver === 'function') {
            devToolObserver === null || devToolObserver === void 0 ? void 0 : devToolObserver(payload);
          }
          instance.sendOperationalEvent(payload);
        });
      }).catch(error => {
        throw error;
      });
    }
    scheduleIdleCallback(onIdle);
  }
  sinkInteractionHandler(sinkFn);
}
function sinkExperimentalInteractionMetrics(instance, payloadPackage) {
  function experimentalMetricsSinkFn(interactionId, interaction) {
    function experimentalMetricsOnIdle() {
      const payloadPromise = payloadPackage.createExperimentalMetricsPayload(interactionId, interaction);
      payloadPromise.then(payload => {
        if (payload) {
          // eslint-disable-next-line @atlaskit/platform/ensure-feature-flag-prefix
          if (fg('enable_ufo_devtools_api_for_extra_events')) {
            // NOTE: This API is used by the UFO DevTool Chrome Extension and Criterion
            const devToolObserver = globalThis.__ufo_devtool_onUfoPayload;
            if (typeof devToolObserver === 'function') {
              devToolObserver === null || devToolObserver === void 0 ? void 0 : devToolObserver(payload);
            }
          }
          instance.sendOperationalEvent(payload);
        }
      });
    }
    scheduleIdleCallback(experimentalMetricsOnIdle);
  }
  sinkExperimentalHandler(experimentalMetricsSinkFn);
}
function sinkPostInteractionLog(instance, createPostInteractionLogPayload) {
  sinkPostInteractionLogHandler(logOutput => {
    scheduleIdleCallback(() => {
      const payload = createPostInteractionLogPayload(logOutput);
      if (payload) {
        // NOTE: This API is used by the UFO DevTool Chrome Extension and also by Criterion
        // eslint-disable-next-line @atlaskit/platform/ensure-feature-flag-prefix
        if (fg('enable_ufo_devtools_api_for_extra_events')) {
          const devToolObserver = globalThis.__ufo_devtool_onUfoPayload;
          if (typeof devToolObserver === 'function') {
            devToolObserver === null || devToolObserver === void 0 ? void 0 : devToolObserver(payload);
          }
        }
        instance.sendOperationalEvent(payload);
      }
    });
  });
}
function sinkInteractionExtraMetrics(instance, createInteractionExtraLogPayload) {
  interactionExtraMetrics.sinkHandler((interactionId, interaction) => {
    scheduleIdleCallback(async () => {
      const payload = await createInteractionExtraLogPayload(interactionId, interaction);
      if (payload) {
        // NOTE: This API is used by the UFO DevTool Chrome Extension and also by Criterion
        // eslint-disable-next-line @atlaskit/platform/ensure-feature-flag-prefix
        if (fg('enable_ufo_devtools_api_for_extra_events')) {
          const devToolObserver = globalThis.__ufo_devtool_onUfoPayload;
          if (typeof devToolObserver === 'function') {
            devToolObserver === null || devToolObserver === void 0 ? void 0 : devToolObserver(payload);
          }
        }
        instance.sendOperationalEvent(payload);
      }
    });
  });
}
export function init(analyticsWebClientAsync, config) {
  var _config$vc;
  if (initialized) {
    return;
  }

  // Initialize pressure observer for CPU usage monitoring
  initialisePressureObserver();
  initialiseMemoryObserver();
  setUFOConfig(config);
  if ((_config$vc = config.vc) !== null && _config$vc !== void 0 && _config$vc.enabled) {
    var _config$experimentalI, _config$extraInteract;
    const vcOptions = {
      heatmapSize: config.vc.heatmapSize,
      oldDomUpdates: config.vc.oldDomUpdates,
      devToolsEnabled: config.vc.devToolsEnabled,
      selectorConfig: config.vc.selectorConfig,
      ssrEnablePageLayoutPlaceholder: config.vc.ssrEnablePageLayoutPlaceholder
    };
    postInteractionLog.initializeVCObserver(vcOptions);
    if (config !== null && config !== void 0 && (_config$experimentalI = config.experimentalInteractionMetrics) !== null && _config$experimentalI !== void 0 && _config$experimentalI.enabled) {
      experimentalVC.initialize(vcOptions).start({
        startTime: 0
      });
    }
    if (config !== null && config !== void 0 && (_config$extraInteract = config.extraInteractionMetrics) !== null && _config$extraInteract !== void 0 && _config$extraInteract.enabled && fg('platform_ufo_enable_ttai_with_3p')) {
      interactionExtraMetrics.initializeVCObserver(vcOptions);
    }
  }
  setupHiddenTimingCapture();
  startLighthouseObserver();
  initialized = true;
  if (typeof PerformanceObserver !== 'undefined') {
    const observer = getPerformanceObserver();
    observer.observe({
      type: 'event',
      buffered: true,
      durationThreshold: 16
    });
  }
  Promise.all([analyticsWebClientAsync, import( /* webpackChunkName: "create-payloads" */'../create-payload'), import( /* webpackChunkName: "create-post-interaction-log-payload" */'../create-post-interaction-log-payload'), import( /* webpackChunkName: "create-interaction-extra-metrics-payload" */'../create-interaction-extra-metrics-payload')]).then(([awc, payloadPackage, createPostInteractionLogPayloadPackage, createInteractionExtraMetricsPayloadPackage]) => {
    if (awc.getAnalyticsWebClientPromise) {
      awc.getAnalyticsWebClientPromise().then(client => {
        var _config$experimentalI2, _config$postInteracti, _config$extraInteract2;
        const instance = client.getInstance();
        sinkInteraction(instance, payloadPackage);
        if (config !== null && config !== void 0 && (_config$experimentalI2 = config.experimentalInteractionMetrics) !== null && _config$experimentalI2 !== void 0 && _config$experimentalI2.enabled) {
          sinkExperimentalInteractionMetrics(instance, payloadPackage);
        }
        if ((_config$postInteracti = config.postInteractionLog) !== null && _config$postInteracti !== void 0 && _config$postInteracti.enabled) {
          sinkPostInteractionLog(instance, createPostInteractionLogPayloadPackage.default);
        }
        if (config !== null && config !== void 0 && (_config$extraInteract2 = config.extraInteractionMetrics) !== null && _config$extraInteract2 !== void 0 && _config$extraInteract2.enabled && fg('platform_ufo_enable_ttai_with_3p')) {
          sinkInteractionExtraMetrics(instance, createInteractionExtraMetricsPayloadPackage.default);
        }
      });
    } else if (awc.sendOperationalEvent) {
      var _config$experimentalI3, _config$postInteracti2, _config$extraInteract3;
      sinkInteraction(awc, payloadPackage);
      if (config !== null && config !== void 0 && (_config$experimentalI3 = config.experimentalInteractionMetrics) !== null && _config$experimentalI3 !== void 0 && _config$experimentalI3.enabled) {
        sinkExperimentalInteractionMetrics(awc, payloadPackage);
      }
      if ((_config$postInteracti2 = config.postInteractionLog) !== null && _config$postInteracti2 !== void 0 && _config$postInteracti2.enabled) {
        sinkPostInteractionLog(awc, createPostInteractionLogPayloadPackage.default);
      }
      if (config !== null && config !== void 0 && (_config$extraInteract3 = config.extraInteractionMetrics) !== null && _config$extraInteract3 !== void 0 && _config$extraInteract3.enabled && fg('platform_ufo_enable_ttai_with_3p')) {
        sinkInteractionExtraMetrics(awc, createInteractionExtraMetricsPayloadPackage.default);
      }
    }
  });
}